import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AffiliateApplicationFormProps {
  userEmail?: string;
  onSuccess?: () => void;
}

export const AffiliateApplicationForm = ({ userEmail, onSuccess }: AffiliateApplicationFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: userEmail || "",
    website: "",
    audience: "",
    trafficSource: "",
    monthlyVisitors: "",
    reason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  // Helper function to normalize website/social media URLs based on traffic source
  const normalizeWebsiteUrl = (input: string, trafficSource: string): string => {
    if (!input || !input.trim()) return '';

    let url = input.trim();

    // Handle @username format based on traffic source
    if (url.startsWith('@')) {
      const username = url.substring(1);

      // Map traffic source to appropriate social platform
      const platformMap: { [key: string]: string } = {
        'twitter': `https://twitter.com/${username}`,
        'instagram': `https://instagram.com/${username}`,
        'tiktok': `https://tiktok.com/@${username}`,
        'youtube': `https://youtube.com/@${username}`,
        'linkedin': `https://linkedin.com/in/${username}`,
      };

      // Use traffic source if it matches a social platform, otherwise default to Twitter
      if (trafficSource && platformMap[trafficSource]) {
        return platformMap[trafficSource];
      }

      // Default to Twitter if no traffic source selected or it's not a social platform
      return `https://twitter.com/${username}`;
    }

    // Handle common social media patterns without protocol
    const socialPatterns = [
      { pattern: /^(twitter\.com|x\.com)\//i, prefix: 'https://' },
      { pattern: /^facebook\.com\//i, prefix: 'https://www.' },
      { pattern: /^instagram\.com\//i, prefix: 'https://www.' },
      { pattern: /^linkedin\.com\//i, prefix: 'https://www.' },
      { pattern: /^youtube\.com\//i, prefix: 'https://www.' },
      { pattern: /^tiktok\.com\//i, prefix: 'https://www.' },
      { pattern: /^github\.com\//i, prefix: 'https://' },
    ];

    for (const { pattern, prefix } of socialPatterns) {
      if (pattern.test(url)) {
        return prefix + url;
      }
    }

    // If it already has a protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Otherwise, assume it's a domain and add https://
    return 'https://' + url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your name",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast({
        title: "Valid email required",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!formData.reason.trim() || formData.reason.trim().length < 20) {
      toast({
        title: "Tell us more",
        description: "Please tell us why you'd like to join (at least 20 characters)",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const normalizedWebsite = normalizeWebsiteUrl(formData.website, formData.trafficSource);

      const applicationData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        website: normalizedWebsite || null,
        audience: formData.audience.trim() || null,
        trafficSource: formData.trafficSource || null,
        monthlyVisitors: formData.monthlyVisitors || null,
        reason: formData.reason.trim(),
        applied_at: new Date().toISOString(),
        status: 'pending',
      };

      // Send via edge function
      const { data, error } = await supabase.functions.invoke('send-affiliate-application', {
        body: applicationData
      });

      if (error) {
        throw new Error(error.message || 'Failed to submit application');
      }

      setIsSuccess(true);

      toast({
        title: "Application submitted!",
        description: "Check your email for confirmation. We'll review and respond within 24-48 hours.",
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 2000);
      }

    } catch (error) {
      toast({
        title: "Submission failed",
        description: error instanceof Error ? error.message : "Please try again or contact support",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="py-8">
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h3 className="text-3xl font-bold mb-3 text-gray-900">Application Submitted!</h3>
          <p className="text-gray-600 mb-6 text-lg">
            Thank you for your interest in our affiliate program.
          </p>
          <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto">
            <p className="text-sm text-gray-600">
              We've sent a confirmation email to
            </p>
            <p className="font-semibold text-gray-900 mt-1">{formData.email}</p>
            <p className="text-sm text-gray-500 mt-4">
              Our team will review your application and respond within 24-48 hours
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center pb-6">
        <h3 className="text-3xl font-bold mb-2 text-gray-900">Apply to Join</h3>
        <p className="text-base text-gray-600">
          Tell us a bit about yourself and why you'd like to partner with us
        </p>
      </div>
      <div className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Required Fields */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-semibold text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={isSubmitting}
              required
              className="h-12 text-base"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={isSubmitting}
              required
              className="h-12 text-base"
            />
            <p className="text-xs text-gray-500">
              We'll send your affiliate link and updates to this email
            </p>
          </div>

          {/* Optional Fields */}
          <div className="space-y-2">
            <Label htmlFor="website" className="text-sm font-semibold text-gray-700">
              Website / Social Media Profile
            </Label>
            <Input
              id="website"
              type="text"
              placeholder="e.g., @username, facebook.com/username, or yourwebsite.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              disabled={isSubmitting}
              className="h-12 text-base"
            />
            <p className="text-xs text-gray-500">
              Enter @username (we'll use your traffic source below), partial URL, or full URL
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="audience" className="text-sm font-semibold text-gray-700">
              Your Audience
            </Label>
            <Input
              id="audience"
              type="text"
              placeholder="e.g., Startup founders, Product managers, Developers"
              value={formData.audience}
              onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
              disabled={isSubmitting}
              className="h-12 text-base"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="trafficSource" className="text-sm font-semibold text-gray-700">
              Primary Traffic Source
            </Label>
            <Select
              value={formData.trafficSource}
              onValueChange={(value) => setFormData({ ...formData, trafficSource: value })}
              disabled={isSubmitting}
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Select your main channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blog">Blog / Content Site</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="twitter">Twitter / X</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="newsletter">Email Newsletter</SelectItem>
                <SelectItem value="podcast">Podcast</SelectItem>
                <SelectItem value="community">Community / Forum</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="monthlyVisitors" className="text-sm font-semibold text-gray-700">
              Estimated Monthly Reach
            </Label>
            <Select
              value={formData.monthlyVisitors}
              onValueChange={(value) => setFormData({ ...formData, monthlyVisitors: value })}
              disabled={isSubmitting}
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-1k">0 - 1,000</SelectItem>
                <SelectItem value="1k-5k">1,000 - 5,000</SelectItem>
                <SelectItem value="5k-10k">5,000 - 10,000</SelectItem>
                <SelectItem value="10k-50k">10,000 - 50,000</SelectItem>
                <SelectItem value="50k-100k">50,000 - 100,000</SelectItem>
                <SelectItem value="100k+">100,000+</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Visitors, subscribers, followers, or monthly impressions
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-semibold text-gray-700">
              Why do you want to join? <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Tell us why you're excited about promoting WillTheyConvert to your audience..."
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              disabled={isSubmitting}
              rows={5}
              required
              className="resize-none text-base"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Minimum 20 characters
              </p>
              <p className={`text-xs ${formData.reason.length >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.reason.length}/20
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">What happens next?</span> You'll receive a confirmation email immediately. Our team reviews all applications within 24-48 hours. If approved, you'll get your unique affiliate link and access to promotional materials.
            </p>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 h-14 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting Application...
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Submit Application
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};
